import React from 'react';
import { Link } from 'react-router-dom';
import { COPY } from '../contracts/copyMap';
import { ROUTES } from '../contracts/routes';
import { CATEGORIES } from '../contracts/taxonomy';
import HairlineDivider from '../components/oracle/HairlineDivider';
import PublicPageLayout from '../components/public/PublicPageLayout';

// Public About / How It Works page. Describes the CURRENT scoring model —
// 7 weighted categories rolled up from 32 subcategory reads (contracts/
// taxonomy.js is the single source for names and weights; never hardcode
// them here). Descriptions live in COPY.ABOUT_CATEGORY_DESCRIPTIONS keyed
// by category key, so a taxonomy change fails loudly in tests.
const STEPS = [
  { headline: COPY.ABOUT_STEP_1_HEADLINE, body: COPY.ABOUT_STEP_1_BODY },
  { headline: COPY.ABOUT_STEP_2_HEADLINE, body: COPY.ABOUT_STEP_2_BODY },
  { headline: COPY.ABOUT_STEP_3_HEADLINE, body: COPY.ABOUT_STEP_3_BODY },
  { headline: COPY.ABOUT_STEP_4_HEADLINE, body: COPY.ABOUT_STEP_4_BODY },
];

const SectionEyebrow = ({ children }) => (
  <h2 className="m-0 font-display uppercase tracking-[0.2em] text-[12px] text-aurum">
    {children}
  </h2>
);

const About = () => (
  <PublicPageLayout
    title={COPY.PAGE_TITLE_ABOUT}
    eyebrow={COPY.ABOUT_EYEBROW}
    headline={COPY.ABOUT_HEADLINE}
  >
    <div className="space-y-5">
      <p className="m-0 font-display text-[17px] leading-relaxed text-bone">
        {COPY.ABOUT_INTRO_1}
      </p>
      <p className="m-0 font-display text-[17px] leading-relaxed text-bone-dim">
        {COPY.ABOUT_INTRO_2}
      </p>
    </div>

    <HairlineDivider />

    <SectionEyebrow>{COPY.ABOUT_PROCESS_HEADLINE}</SectionEyebrow>
    <ol className="mt-8 m-0 list-none space-y-9 p-0">
      {STEPS.map((step, i) => (
        <li key={step.headline} className="flex gap-6">
          <span className="font-display text-[15px] leading-snug text-aurum tabular-nums">
            {String(i + 1).padStart(2, '0')}
          </span>
          <div>
            <h3 className="m-0 font-display uppercase tracking-[0.12em] text-[15px] text-bone">
              {step.headline}
            </h3>
            <p className="mt-2 m-0 font-display text-[15px] leading-relaxed text-bone-dim">
              {step.body}
            </p>
          </div>
        </li>
      ))}
    </ol>

    <HairlineDivider />

    <SectionEyebrow>{COPY.ABOUT_CATEGORIES_HEADLINE}</SectionEyebrow>
    <p className="mt-4 m-0 font-display text-[15px] leading-relaxed text-bone-dim">
      {COPY.ABOUT_CATEGORIES_INTRO}
    </p>
    <ul className="mt-8 m-0 list-none p-0">
      {CATEGORIES.map((category) => (
        <li
          key={category.key}
          className="border-t border-hairline-soft py-6 first:border-t-0"
        >
          <div className="flex items-baseline justify-between gap-4">
            <h3 className="m-0 font-display text-[19px] text-bone">
              {category.label}
            </h3>
            <span className="font-display text-[13px] tracking-[0.08em] text-aurum tabular-nums">
              {Math.round(category.weight * 100)}%
            </span>
          </div>
          <p className="mt-2 m-0 font-display text-[15px] leading-relaxed text-bone-dim">
            {COPY.ABOUT_CATEGORY_DESCRIPTIONS[category.key]}
          </p>
        </li>
      ))}
    </ul>

    <HairlineDivider />

    <SectionEyebrow>{COPY.ABOUT_MODIFIABILITY_HEADLINE}</SectionEyebrow>
    <p className="mt-4 m-0 font-display text-[15px] leading-relaxed text-bone">
      {COPY.ABOUT_MODIFIABILITY_BODY}
    </p>
    <p className="mt-6 m-0 font-display italic text-[14px] leading-relaxed text-bone-dim">
      {COPY.ABOUT_DISCLAIMER}
    </p>

    <div className="mt-16">
      <Link
        to={ROUTES.SIGN_IN}
        className="inline-block border border-aurum px-6 py-3 font-display uppercase text-[13px] tracking-[0.2em] text-aurum no-underline transition-colors hover:bg-aurum hover:text-obsidian"
      >
        {COPY.ABOUT_CTA}
      </Link>
    </div>
  </PublicPageLayout>
);

export default About;
